import { useSyncExternalStore } from "react";
import { FirebaseError, initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from "firebase/auth";
import { client as fieldRepClient } from "@labeling-tools/api/field-rep/generated/client.gen";

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/missing-email": "Please enter an email address.",
  "auth/missing-password": "Please enter a password.",
  "auth/operation-not-allowed":
    "This login method is not enabled. Please contact support.",
  "auth/user-not-found": "No account found with this email address.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/invalid-password": "Incorrect password. Please try again.",
  "auth/invalid-credential": "Invalid email or password. Please try again.",
  "auth/user-disabled":
    "This account has been disabled. Please contact support.",
  "auth/account-exists-with-different-credential":
    "An account already exists with a different login method for this email.",
  "auth/too-many-requests": "Too many failed attempts. Please try again later.",
  "auth/network-request-failed":
    "Network error. Please check your internet connection and try again.",
  "auth/popup-blocked":
    "Pop-up was blocked by your browser. Please allow pop-ups and try again.",
  "auth/popup-closed-by-user": "Login was cancelled. Please try again.",
  "auth/cancelled-popup-request": "Only one login request allowed at a time.",
  "auth/unauthorized-domain":
    "This domain is not authorized for OAuth operations.",
};

const FALLBACK_LOGIN_ERROR_MESSAGE =
  "An unexpected error occurred. Please try again.";

const firebaseApp = initializeApp(
  JSON.parse(import.meta.env.VITE_FIELD_REP_FIREBASE_CONFIG),
);

const auth = getAuth(firebaseApp);

fieldRepClient.interceptors.request.use(async (request) => {
  const user = auth.currentUser;
  if (user) {
    request.headers.set("Authorization", `Bearer ${await user.getIdToken()}`);
  }
  return request;
});

let authUser: User | null | undefined;
const authUserListeners = new Set<() => void>();

onAuthStateChanged(auth, (user) => {
  authUser = user;
  authUserListeners.forEach((listener) => listener());
});

const subscribeToAuthUser = (listener: () => void) => {
  authUserListeners.add(listener);
  return () => {
    authUserListeners.delete(listener);
  };
};

export const useAuthUser = (): User | null | undefined =>
  useSyncExternalStore(subscribeToAuthUser, () => authUser);

export const loginErrorMessage = (error: unknown): string =>
  (error instanceof FirebaseError && LOGIN_ERROR_MESSAGES[error.code]) ||
  FALLBACK_LOGIN_ERROR_MESSAGE;

export const loginWithEmail = async (
  email: string,
  password: string,
): Promise<void> => {
  await signInWithEmailAndPassword(auth, email, password);
};

export const loginWithGoogle = async (): Promise<void> => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(auth, provider);
};

export const logout = (): Promise<void> => auth.signOut();
